// Retry-once wrapper for dynamic imports of code-split chunks.
//
// Why this exists (2026-02 Phase B closeout, item 1):
// The lazy dialogs and the export helper are all fetched from
// `static/js/*.chunk.js` on FIRST interaction.  On flaky networks
// (mobile hotspots, tab-suspend/resume, cold Cloudflare edges) or
// when a deploy has just landed a new bundle while a tab is still
// pointed at the old `main.<oldhash>.js`, that first fetch can 404
// or drop, surfacing as `ChunkLoadError: Loading chunk N failed`.
// Firefox is particularly sensitive on cold-loads under an SW-
// blocked context.
//
// This helper wraps every `import(/* webpackChunkName */ ...)` with
// a single retry after a short delay.  If both attempts fail, the
// underlying error is re-thrown verbatim so `React.lazy`'s error
// boundary + `doExport`'s try/catch behave identically to before.
// The net effect: a transient chunk failure on the very first
// interaction resolves silently on retry instead of surfacing as a
// user-visible toast / "Export failed" state.
//
// Zero cost on the happy path — the retry closure is only invoked
// when the first import rejects.
const CHUNK_LOAD_ERR_RE = /(ChunkLoadError|Loading chunk .* failed|Failed to fetch dynamically imported module)/i;

export function loadChunkWithRetry(importer, { retries = 1, delayMs = 400 } = {}) {
  return new Promise((resolve, reject) => {
    const attempt = (remaining) => {
      importer()
        .then(resolve)
        .catch((err) => {
          // Only retry on the specific "chunk fetch failed" family —
          // don't paper over a real error in the module body.
          const isChunkErr = err && (err.name === "ChunkLoadError" || CHUNK_LOAD_ERR_RE.test(String(err && err.message)));
          if (remaining > 0 && isChunkErr) {
            setTimeout(() => attempt(remaining - 1), delayMs);
          } else {
            reject(err);
          }
        });
    };
    attempt(retries);
  });
}
